import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Container, Paper, TextField, Button, Typography, List, ListItem, ListItemText,
    CircularProgress, IconButton, AppBar, Toolbar, Avatar, Alert, Chip, LinearProgress, AlertTitle,
    FormControl, InputLabel, Select, MenuItem, Fab, ToggleButtonGroup, ToggleButton, Tooltip
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Send as SendIcon,
    Person as PersonIcon,
    SmartToy as SmartToyIcon,
    Info as InfoIcon,
    Upload as UploadIcon,
    FileUpload as FileUploadIcon,
    Add as AddIcon,
    AttachFile as AttachFileIcon
} from '@mui/icons-material';
import axios from 'axios';

// Use relative path for Azure Static Web Apps
const SERVER_URL = '/api';

// Create a custom axios instance with increased timeout
const apiClient = axios.create({
    baseURL: SERVER_URL,
    timeout: 120000, // 2 minute timeout for large documents
    maxContentLength: 50 * 1024 * 1024, // 50MB max response size
    headers: {
        'Content-Type': 'application/json',
    }
});

interface Message {
    id: string;
    sender: 'user' | 'llm' | 'system';
    text: string;
    timestamp: Date;
    simulated?: boolean;
}

const DocumentChatPage: React.FC = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [loadingStartTime, setLoadingStartTime] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [docDisplayName, setDocDisplayName] = useState<string>('No Document Selected');
    const [isSimulated, setIsSimulated] = useState<boolean>(false);
    const [modelUsed, setModelUsed] = useState<string>('');
    const [documentUploaded, setDocumentUploaded] = useState<boolean>(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadedDocPath, setUploadedDocPath] = useState<string | null>(null);
    const messagesEndRef = useRef<null | HTMLDivElement>(null);
    const [loadingElapsedSeconds, setLoadingElapsedSeconds] = useState(0);
    const [modelPreference, setModelPreference] = useState<'gemini' | 'claude'>('claude');

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    useEffect(() => {
        // Initialize with a welcome message
        setMessages([
            {
                id: `system-${Date.now()}`,
                sender: 'system',
                text: `Welcome to Document Chat. Please upload a document to begin chatting.`,
                timestamp: new Date(),
            },
        ]);
    }, []);

    // Update the loading timer every second
    useEffect(() => {
        let timerInterval: NodeJS.Timeout | null = null;

        if (isLoading && loadingStartTime) {
            timerInterval = setInterval(() => {
                const elapsedSeconds = Math.floor((Date.now() - loadingStartTime) / 1000);
                setLoadingElapsedSeconds(elapsedSeconds);
            }, 1000);
        } else {
            setLoadingElapsedSeconds(0);
        }

        return () => {
            if (timerInterval) clearInterval(timerInterval);
        };
    }, [isLoading, loadingStartTime]);

    const handleSendMessage = async () => {
        if (inputValue.trim() === '') return;

        // Check if document has been uploaded
        if (!documentUploaded || !uploadedDocPath) {
            setMessages((prevMessages) => [
                ...prevMessages,
                {
                    id: `system-upload-required-${Date.now()}`,
                    sender: 'system',
                    text: `Please upload a document first to begin chatting.`,
                    timestamp: new Date()
                }
            ]);
            return;
        }

        const userMessage: Message = {
            id: `user-${Date.now()}`,
            sender: 'user',
            text: inputValue,
            timestamp: new Date(),
        };
        setMessages((prevMessages) => [...prevMessages, userMessage]);
        const currentInput = inputValue;
        setInputValue('');
        setIsLoading(true);
        setLoadingProgress(0);
        setLoadingStartTime(Date.now()); // Record when loading started
        setError(null);

        // Set up a timer to simulate progress
        const progressInterval = setInterval(() => {
            setLoadingProgress((prevProgress) => {
                // Slowly increase progress but never reach 100%
                // The speed decreases as we get closer to 100%
                const remaining = 100 - prevProgress;
                const increment = remaining * 0.03; // Larger at first, smaller as we approach 100%
                return Math.min(prevProgress + increment, 98); // Never quite reach 100%
            });
        }, 300);

        try {
            // Prepare chat history for the LLM
            const historyForLLM = messages
                .filter(msg => msg.sender === 'user' || msg.sender === 'llm')
                .map(msg => ({
                    role: msg.sender === 'user' ? 'user' : 'assistant',
                    content: msg.text
                }));

            // Add the current user message to the history being sent
            historyForLLM.push({ role: 'user', content: currentInput });

            // Debug logging before sending request
            console.log('=== CHAT REQUEST DEBUG ===');
            console.log('documentType:', 'uploaded');
            console.log('documentPath (uploadedDocPath):', uploadedDocPath);
            console.log('userMessage (currentInput):', currentInput);
            console.log('chatHistory length:', historyForLLM.slice(0, -1).length);
            console.log('documentUploaded state:', documentUploaded);

            const requestPayload = {
                documentType: 'uploaded',
                documentPath: uploadedDocPath, // Send the uploaded document path
                userMessage: currentInput, // Send the current message explicitly
                chatHistory: historyForLLM.slice(0, -1), // Send history *before* current message
                modelPreference: modelPreference // Send user's model preference
            };

            console.log('Full request payload:', JSON.stringify(requestPayload, null, 2));

            const response = await apiClient.post('/ChatFunction', requestPayload);

            // Check if response includes a document title
            if (response.data.documentTitle) {
                setDocDisplayName(response.data.documentTitle);
            }

            // Check if this is a simulated response
            if (response.data.simulated) {
                setIsSimulated(true);
            }

            // Get model information if available
            if (response.data.modelUsed) {
                setModelUsed(response.data.modelUsed);
            }

            const llmResponse: Message = {
                id: `llm-${Date.now()}`,
                sender: 'llm',
                text: response.data.llmResponse,
                timestamp: new Date(),
                simulated: response.data.simulated
            };
            setMessages((prevMessages) => [...prevMessages, llmResponse]);
        } catch (err: any) {
            console.error('Error sending message or receiving LLM response:', err);
            const errorMessage = err.response?.data?.error || err.message || 'Failed to get response from the assistant.';
            setError(errorMessage);
            setMessages((prevMessages) => [...prevMessages, {
                id: `system-error-${Date.now()}`,
                sender: 'system',
                text: `Error: ${errorMessage}`,
                timestamp: new Date()
            }]);
        } finally {
            clearInterval(progressInterval); // Clean up the interval
            setLoadingProgress(0); // Reset progress
            setLoadingStartTime(null); // Reset loading start time
            setIsLoading(false);
        }
    };

    // Function to trigger file input click
    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    // Function to handle file selection
    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const file = files[0];
        setDocDisplayName(file.name);
        setIsUploading(true);
        setUploadProgress(0);
        setError(null);

        const uploadInterval = setInterval(() => {
            setUploadProgress(prev => {
                // Simulate progress until we get actual progress
                const increment = (100 - prev) * 0.05;
                return Math.min(prev + increment, 95); // Never reach 100% until done
            });
        }, 300);

        // Create FormData to send the file
        const formData = new FormData();
        formData.append('file', file);

        try {
            // Use the correct Azure Function endpoint
            const uploadResponse = await axios.post(`${SERVER_URL}/UploadDocumentFunction`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadProgress(progress);
                    }
                }
            });

            if (uploadResponse.data.success) {
                console.log('=== UPLOAD SUCCESS DEBUG ===');
                console.log('Upload response data:', uploadResponse.data);
                console.log('Setting uploadedDocPath to:', uploadResponse.data.filePath);

                setDocumentUploaded(true);
                setUploadProgress(100);
                setUploadedDocPath(uploadResponse.data.filePath); // Store the server-side path

                console.log('Document uploaded state set to true');
                console.log('Uploaded doc path set to:', uploadResponse.data.filePath);

                // Add system message about document being ready
                setMessages(prevMessages => [
                    ...prevMessages,
                    {
                        id: `system-upload-${Date.now()}`,
                        sender: 'system',
                        text: `Document "${file.name}" has been successfully uploaded and is ready for chat.`,
                        timestamp: new Date()
                    }
                ]);

                // If there's a title in the response, update it
                if (uploadResponse.data.documentTitle) {
                    setDocDisplayName(uploadResponse.data.documentTitle);
                }
            } else {
                throw new Error(uploadResponse.data.error || 'Failed to upload document');
            }
        } catch (err: any) {
            console.error('Error uploading document:', err);
            const errorMessage = err.response?.data?.error || err.message || 'Failed to upload the document.';
            setError(errorMessage);
            setMessages(prevMessages => [
                ...prevMessages,
                {
                    id: `system-error-${Date.now()}`,
                    sender: 'system',
                    text: `Error uploading document: ${errorMessage}`,
                    timestamp: new Date()
                }
            ]);
        } finally {
            clearInterval(uploadInterval);
            setIsUploading(false);

            // Clear the file input value to allow re-selection of the same file
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 54px)', bgcolor: 'background.default' }}>
            <AppBar position="static" sx={{ bgcolor: 'background.paper', color: 'text.primary', boxShadow: 'none', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
                <Toolbar>
                    <IconButton edge="start" color="inherit" onClick={() => navigate('/')} sx={{ mr: 2 }} aria-label="back to search">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {documentUploaded ? `Chat: ${docDisplayName}` : 'Document Chat'}
                    </Typography>
                    {documentUploaded && (
                        <Box sx={{ mr: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                                Select Model
                            </Typography>
                            <ToggleButtonGroup
                                value={modelPreference}
                                exclusive
                                onChange={(e, newValue) => {
                                    if (newValue !== null) {
                                        setModelPreference(newValue);
                                    }
                                }}
                                size="small"
                                sx={{
                                    '& .MuiToggleButton-root': {
                                        px: 2,
                                        py: 0.5,
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        textTransform: 'none',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        color: 'text.secondary',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: 'primary.main',
                                            color: 'primary.contrastText',
                                            borderColor: 'primary.main',
                                            '&:hover': {
                                                backgroundColor: 'primary.dark',
                                            },
                                        },
                                    },
                                }}
                            >
                                <ToggleButton value="claude">
                                    <Box
                                        component="img"
                                        src={`${process.env.PUBLIC_URL}/claude.png`}
                                        alt="Claude"
                                        sx={{ width: 20, height: 20, mr: 1, objectFit: 'contain' }}
                                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                    />
                                    Claude 3.5
                                </ToggleButton>
                                <ToggleButton value="gemini">
                                    <Box
                                        component="img"
                                        src={`${process.env.PUBLIC_URL}/google.png`}
                                        alt="Google"
                                        sx={{ width: 20, height: 20, mr: 1, objectFit: 'contain' }}
                                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                    />
                                    Gemini 2.5
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    )}
                    {documentUploaded && (
                        <Chip
                            label={`Model: ${modelPreference === 'gemini' ? 'Gemini 2.5 Flash' : 'Claude 3.5 Sonnet'}`}
                            color="primary"
                            size="small"
                            sx={{ mr: 1 }}
                            title={`Selected AI model`}
                        />
                    )}
                    {isSimulated && (
                        <Chip
                            label="DEMO MODE"
                            color="warning"
                            size="small"
                            sx={{ mr: 1 }}
                            title="The LLM API is not configured. Responses are simulated."
                        />
                    )}
                </Toolbar>
                {isUploading && (
                    <LinearProgress
                        variant="determinate"
                        value={uploadProgress}
                        sx={{ height: 4 }}
                    />
                )}
            </AppBar>

            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: { xs: 1, sm: 2 } }}>
                {!documentUploaded && (
                    <Alert
                        severity="info"
                        sx={{ mb: 2, display: 'flex', alignItems: 'center' }}
                        action={
                            <Button
                                color="info"
                                size="small"
                                variant="outlined"
                                startIcon={<UploadIcon />}
                                onClick={handleUploadClick}
                                disabled={isUploading}
                            >
                                {isUploading ? "Uploading..." : "Upload Document"}
                            </Button>
                        }
                    >
                        <strong>Please upload a document to begin chatting.</strong>
                    </Alert>
                )}

                <List>
                    {messages.map((msg) => (
                        <ListItem
                            key={msg.id}
                            sx={{
                                display: 'flex',
                                justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                                mb: 1.5,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', maxWidth: '80%' }}>
                                {msg.sender === 'llm' && (
                                    <Avatar sx={{ bgcolor: msg.simulated ? 'warning.main' : 'secondary.main', mr: 1.5, width: 32, height: 32 }}>
                                        <SmartToyIcon fontSize="small" />
                                    </Avatar>
                                )}
                                {msg.sender === 'system' && (
                                    <Avatar sx={{ bgcolor: 'grey.600', mr: 1.5, width: 32, height: 32 }}>
                                        <InfoIcon fontSize="small" />
                                    </Avatar>
                                )}
                                <Paper
                                    elevation={2}
                                    sx={{
                                        p: 1.5,
                                        bgcolor: msg.sender === 'user' ? 'primary.main' : (msg.sender === 'system' ? 'background.paper' : '#262D31'), // Slightly different dark for LLM
                                        color: msg.sender === 'user' ? 'primary.contrastText' : 'text.primary',
                                        borderRadius: msg.sender === 'user' ? '20px 20px 5px 20px' : '5px 20px 20px 20px',
                                        border: msg.sender === 'system' ? '1px dashed grey.500' : (msg.simulated ? '1px dashed orange' : 'none'),
                                    }}
                                >
                                    <ListItemText
                                        primary={<Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{msg.text}</Typography>}
                                        secondary={
                                            <>
                                                {msg.simulated && (
                                                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                                                        Simulated response (API not configured)
                                                    </Typography>
                                                )}
                                                {msg.sender !== 'system' &&
                                                    msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                                }
                                            </>
                                        }
                                        secondaryTypographyProps={{ color: msg.sender === 'user' ? 'rgba(255,255,255,0.7)' : 'text.secondary', fontSize: '0.75rem', textAlign: msg.sender === 'user' ? 'right' : 'left', mt: 0.5 }}
                                    />
                                </Paper>
                                {msg.sender === 'user' && (
                                    <Avatar sx={{ bgcolor: 'primary.dark', ml: 1.5, width: 32, height: 32 }}>
                                        <PersonIcon fontSize="small" />
                                    </Avatar>
                                )}
                            </Box>
                        </ListItem>
                    ))}
                    <div ref={messagesEndRef} />
                </List>
            </Box>

            {error && !messages.find(m => m.text.includes(error)) && ( // Show error as an alert if not already in messages
                <Alert severity="error" sx={{ m: { xs: 1, sm: 2 }, mb: 1 }}>{error}</Alert>
            )}

            {isSimulated && messages.length <= 2 && (
                <Alert severity="warning" sx={{ m: { xs: 1, sm: 2 }, mb: 1 }}>
                    The LLM API is not configured. You're seeing simulated responses. To enable full functionality, configure the OPENROUTER_API_KEY in the server .env file.
                </Alert>
            )}

            {isLoading && (
            <Box sx={{ px: { xs: 1, sm: 2 }, pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            {modelPreference === 'gemini' && (
                <Box component="img" src={`${process.env.PUBLIC_URL}/google.png`} alt="Google" sx={{ width: 16, height: 16 }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            )}
            {modelPreference === 'claude' && (
                <Box component="img" src={`${process.env.PUBLIC_URL}/claude.png`} alt="Claude" sx={{ width: 16, height: 16 }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            )}
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            {modelPreference === 'gemini' ? 'Gemini 2.5 Flash' : 'Claude 3.5 Sonnet'}
            {loadingStartTime && (
            <> • {loadingElapsedSeconds}s</>
            )}
            </Typography>
            </Box>
                    <LinearProgress
                        variant="determinate"
                        value={loadingProgress}
                        sx={{
                            height: 4,
                            borderRadius: 2,
                            '& .MuiLinearProgress-bar': {
                                transition: 'transform 0.3s linear'
                            }
                        }}
                    />
                </Box>
            )}

            <Paper square elevation={3} sx={{ p: { xs: 1, sm: 2 }, bgcolor: 'background.paper', borderTop: '1px solid rgba(255,255,255,0.12)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Ask a question about the document..."
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter' && !e.shiftKey && !isLoading) { e.preventDefault(); handleSendMessage(); } }}
                        disabled={isLoading}
                        multiline
                        maxRows={4}
                        sx={{ mr: 1 }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSendMessage}
                        disabled={isLoading || inputValue.trim() === '' || !documentUploaded}
                        sx={{ height: '56px', px: 3 }}
                        startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                    >
                        Send
                    </Button>
                </Box>
            </Paper>

            {/* Hidden file input */}
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.txt,.rtf"
            />

            <Typography
                variant="subtitle2"
                color="textSecondary"
                style={{ marginTop: '4px' }}
                title={`Using OpenRouter with Claude model`}
            >
                {isSimulated ? (
                    <Alert severity="info" style={{ fontSize: '0.8rem', marginTop: '8px' }}>
                        <AlertTitle>Simulated Response</AlertTitle>
                        This is a simulated response. Real responses would use Claude 3 Haiku.
                    </Alert>
                ) : (
                    <Chip
                        label={modelPreference === 'gemini' ? 'Gemini 2.5 Flash' : 'Claude 3.5 Sonnet'}
                        size="small"
                        style={{
                            marginTop: '4px',
                            backgroundColor: '#E0F7FA',
                            color: '#006064'
                        }}
                    />
                )}
            </Typography>

            {isSimulated && (
                <Typography variant="caption" color="textSecondary" style={{ marginTop: '8px', display: 'block' }}>
                    <InfoIcon fontSize="small" style={{ fontSize: '14px', verticalAlign: 'middle', marginRight: '4px' }} />
                    The LLM API is not configured. You're seeing simulated responses. To enable full functionality, configure the OPENROUTER_API_KEY in the server .env file.
                </Typography>
            )}
        </Box>
    );
};

export default DocumentChatPage; 